<template>
  <div class="sandbox">
    <iframe
      id="bannerIframe"
      frameborder="0"
      :src="iframeURL"
      height="100%"
      width="100%"
      allow="clipboard-read; clipboard-write"></iframe>
    <input class="d-none" type="file" @change="onImageSelected" tabindex="-1" ref="imageInput" accept="image/*" />
    <!-- <input class="d-none" type="file" @change="onVideoSelected" tabindex="-1" ref="videoInput" accept="video/mp4, video/webm, video/ogg" /> -->
  </div>
</template>
<script setup lang="ts">
import { fetchBannerById, updateBannerById } from '@/api/banner';
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useUserStore } from '@/stores/user';
import translator from '@/locale/translator';
import { request, requestMultipart } from '@/api/client';
import type { ImageObject } from '@/types/types';
import { fetchVideoMeta } from '@/api/video';

const userStore = useUserStore();
const toast = useToast();
const route = useRoute();
const { jobId, bannerId } = route.query;

const imageInput = ref();
// const videoInput = ref();

onMounted(() => {
  if (!bannerId || !jobId) {
    toast.error('Banner ID is required');
    return;
  }
  window.addEventListener('message', receiveMessage, false);
});
onBeforeUnmount(() => {
  window.removeEventListener('message', receiveMessage, false);
});

/* -------------------------------- COMPUTED -------------------------------- */
const iframeURL = computed(() => {
  if (!bannerId || !jobId) {
    return '';
  }

  const host = encodeURIComponent(import.meta.env.VITE_IFRAME_URL || window.location.origin);

  const userData = JSON.stringify({
    name: userStore.currentUser?.firstname + ' ' + userStore.currentUser?.lastname,
    email: userStore.currentUser?.email,
  });

  return `${userStore.sandboxUrl}?host=${host}&user=${encodeURIComponent(userData)}`;
});

/* --------------------------------- METHODS -------------------------------- */
async function fetchCustomFonts() {
  const bannerIframe = document.getElementById('bannerIframe') as HTMLIFrameElement;
  if (!bannerIframe) return;

  try {
    const response = await request('GET', '/v1/account/custom-fonts');
    const fontsArray = Array.isArray(response.data) ? response.data : [];
    bannerIframe.contentWindow?.postMessage({ type: 'receiveCustomFonts', value: fontsArray }, '*');
  } catch (error) {
    console.error('Failed to fetch custom fonts:', error);
  }
}

async function receiveMessage(event: MessageEvent) {
  const bannerIframe = document.getElementById('bannerIframe') as HTMLIFrameElement;
  if (!event?.data || !bannerIframe) return;
  if (typeof event.data === 'object') {
    if (event.data.type === 'updateBannerData' && event.data.value?.views) {
      try {
        await updateBannerById(bannerId as string, event.data.value);
        bannerIframe.contentWindow?.postMessage('bannerDataSaved', '*');
      } catch (e) {
        bannerIframe.contentWindow?.postMessage('bannerDataNotSaved', '*');
        console.error('Banner error', e);
      }
    } else if (event.data.type === 'postVideoThumbnailUrl') {
      const responseData = {
        type: 'receiveVideoThumbnailUrl',
        value: { hash: event.data.hash, thumbnailURL: undefined },
      };
      try {
        if (event.data.hash) {
          const res = await fetchVideoMeta(event.data.hash);
          responseData.value.thumbnailURL = res;
          bannerIframe.contentWindow?.postMessage(responseData, '*');
        }
      } catch {
        toast.error('Failed to get the video thumbnail');
      }
    }
    // else if (event.data.type === 'uploadDroppedImages') {
    // }
  } else if (event.data === 'getBannerData') {
    try {
      const response = await fetchBannerById(bannerId as string);
      const responseData = { type: 'receiveBannerData', value: response };
      bannerIframe.contentWindow?.postMessage(responseData, '*');
      fetchCustomFonts();
    } catch (error) {
      //TODO translations
      toast.error(`Failed to get banner data: ${error}`);
      bannerIframe.contentWindow?.postMessage('errorBannerData', '*');
    }
  } else if (event.data === 'openImagePicker') {
    imageInput.value.click();
  } else if (event.data === 'openVideoPicker') {
    toast.info('This feature is not functional');
    // videoInput.value.click()
  }
}

async function onImageSelected(e: any) {
  const bannerIframe = document.getElementById('bannerIframe') as HTMLIFrameElement;
  if (!bannerIframe) return;
  try {
    if (!e.target?.files?.length) throw Error('No file found');

    const maxBytes = 10_000_000; // 10MB
    const imageFile = e.target.files[0] as File;

    if (!imageFile.type.includes('image/')) return toast.error(translator.get('order.image.invalid_format'));
    if (imageFile.size > maxBytes) return toast.error(translator.get('order.image.too_big'));

    const formData = new FormData();
    formData.append('image', imageFile);

    const res: { data: ImageObject } = await requestMultipart('POST', '/v1/account/media/images/upload', {}, formData);
    if (res.data.hash) return bannerIframe.contentWindow?.postMessage({ type: 'imageHash', value: res.data.hash }, '*');
    throw Error('Failed to extract image hash');
  } catch (error) {
    console.error(error);
    toast.error(translator.get('data.upload_failed'));
  }
}

// async function onVideoSelected(e: any) {
//   const bannerIframe = document.getElementById('bannerIframe') as HTMLIFrameElement;
//   if (!bannerIframe) return;
//   try {
//     if (!e.target?.files?.length) throw Error('No file found')

//     const maxBytes = 10_000_000; // 10MB
//     const videoFile = e.target.files[0] as File;

//     if (!videoFile.type.includes('video/')) return toast.error(translator.get('order.image.invalid_format'));
//     if (videoFile.size > maxBytes) return toast.error(translator.get('order.image.too_big'));

//     const formData = new FormData();
//     formData.append('video', videoFile);

//     const res: { data: ImageObject } = await requestMultipart('POST', '/v1/account/media/video/upload', { filename: videoFile.name }, formData);
//     bannerIframe.contentWindow?.postMessage({ type: 'imageHash', value: res.data.hash }, '*');
//   } catch (error) {
//     console.error(error);
//     toast.error(translator.get('data.upload_failed'));
//   }

// }
</script>
